import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaEdit,
  FaExternalLinkAlt,
  FaRobot,
  FaTags,
  FaRegCommentDots,
  FaLink,
  FaUserCheck,
  FaBolt,
} from "react-icons/fa";
import { getAutomationById } from "../../services/automationService";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { INFLUENCER_BASE } from "../../constants";

const AUTOMATION_TYPE_LABEL = {
  DM: "Direct Message",
  Comments: "Comments",
  Both: "DM & Comments",
};

const AutomationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [automation, setAutomation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    fetchAutomation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAutomation = async () => {
    try {
      setLoading(true);
      const response = await getAutomationById(id);

      if (response.success && response.data) {
        setAutomation(response.data);
      } else {
        toast.error(response.error || "Failed to load automation details");
        navigate(`${INFLUENCER_BASE}/dashboard/automation-dashboard`);
      }
    } catch (error) {
      console.error("Error loading automation details:", error);
      toast.error(error.error || error.message || "Failed to load automation details");
      navigate(`${INFLUENCER_BASE}/dashboard/automation-dashboard`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <LoadingSpinner message="Loading automation details..." />
      </div>
    );
  }

  if (!automation) return null;

  const typeLabel =
    AUTOMATION_TYPE_LABEL[automation.typeOfAutomation?.type] ||
    automation.typeOfAutomation?.type ||
    "—";
  const gate = automation.followerGate || {};
  const createdAt = automation.createdAt
    ? new Date(automation.createdAt).toLocaleString()
    : null;
  const updatedAt = automation.updatedAt
    ? new Date(automation.updatedAt).toLocaleString()
    : null;

  return (
    <div className="p-4 lg:p-6">
      <button
        type="button"
        onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/automation-dashboard`)}
        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition hover:text-orange-600"
      >
        <FaArrowLeft className="h-3.5 w-3.5" />
        Back to Dashboard
      </button>

      <div className="mb-6 flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-xl font-bold text-gray-900 md:text-2xl lg:text-3xl">
              {automation.uploadPost?.title || "Untitled"}
            </h1>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${
                automation.isActive
                  ? "bg-emerald-50 text-emerald-700 ring-emerald-100"
                  : "bg-gray-100 text-gray-500 ring-gray-200"
              }`}
            >
              <FaBolt className="h-3 w-3" />
              {automation.isActive ? "Active" : "Inactive"}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Full automation configuration and messaging details.
          </p>
        </div>

        {automation.isActive && (
          <button
            type="button"
            onClick={() =>
              navigate(`${INFLUENCER_BASE}/dashboard/instagram-automation/${automation._id}`)
            }
            className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-orange-600 md:w-auto"
          >
            <FaEdit className="h-3.5 w-3.5" />
            Edit Automation
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <DetailCard title="Post" icon={FaRobot}>
            <DetailRow label="Title" value={automation.uploadPost?.title || "Untitled"} />
            <DetailRow
              label="Description"
              value={automation.uploadPost?.description || ""}
              multiline
              emptyText="No description"
            />
            {automation.uploadPost?.mediaLink && (
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-4">
                <span className="w-40 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Media Link
                </span>
                <a
                  href={automation.uploadPost.mediaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 break-all text-sm font-medium text-orange-600 hover:text-orange-700"
                >
                  Open post
                  <FaExternalLinkAlt className="h-3 w-3 shrink-0" />
                </a>
              </div>
            )}
          </DetailCard>

          <DetailCard title="Automation Setup" icon={FaTags}>
            <DetailRow label="Type" value={typeLabel} />
            <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-4">
              <span className="w-40 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400">
                Keywords
              </span>
              <div className="flex flex-wrap gap-1.5">
                {automation.keywords?.length > 0 ? (
                  automation.keywords.map((keyword, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700"
                    >
                      {keyword}
                    </span>
                  ))
                ) : (
                  <span className="text-sm italic text-gray-400">No keywords</span>
                )}
              </div>
            </div>
          </DetailCard>

          <DetailCard title="Message" icon={FaRegCommentDots}>
            <DetailRow
              label="DM / Reply Message"
              value={automation.message || ""}
              multiline
              emptyText="No message"
            />
            {automation.productLink && (
              <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-4">
                <span className="w-40 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Product Link
                </span>
                <a
                  href={automation.productLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 break-all text-sm font-medium text-orange-600 hover:text-orange-700"
                >
                  <FaLink className="h-3 w-3 shrink-0" />
                  {automation.productLink}
                </a>
              </div>
            )}
            <DetailRow
              label="Reply to Comment First"
              value={automation.sendReplyToCommentFirst ? "Yes" : "No"}
            />
            {automation.sendReplyToCommentFirst && (
              <DetailRow
                label="Comment Reply"
                value={automation.replyCommentMessage || ""}
                multiline
                emptyText="No comment reply message"
              />
            )}
          </DetailCard>
        </div>

        <div className="space-y-6">
          {gate.enabled && (
            <DetailCard title="Follower Gate" icon={FaUserCheck}>
              <DetailRow label="Enabled" value="Yes" />
              <DetailRow
                label="Access Prompt"
                value={gate.accessPromptMessage || ""}
                multiline
                emptyText="—"
              />
              <DetailRow label="Access Button" value={gate.accessButtonTitle || "—"} />
              <DetailRow
                label="Follow Prompt"
                value={gate.followPromptMessage || ""}
                multiline
                emptyText="—"
              />
              <DetailRow label="Visit Profile Button" value={gate.visitProfileButtonTitle || "—"} />
              <DetailRow label="Following Button" value={gate.followingButtonTitle || "—"} />
              {gate.profileUrl && (
                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-4">
                  <span className="w-40 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    Profile URL
                  </span>
                  <a
                    href={gate.profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 break-all text-sm font-medium text-orange-600 hover:text-orange-700"
                  >
                    {gate.profileUrl}
                    <FaExternalLinkAlt className="h-3 w-3 shrink-0" />
                  </a>
                </div>
              )}
            </DetailCard>
          )}

          {(createdAt || updatedAt) && (
            <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4 text-xs text-gray-400 shadow-sm">
              {createdAt && <p>Created: {createdAt}</p>}
              {updatedAt && <p className="mt-0.5">Updated: {updatedAt}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DetailCard = ({ title, icon: Icon, children }) => (
  <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
    <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/80 px-5 py-3">
      <Icon className="h-3.5 w-3.5 text-orange-600" />
      <h2 className="text-sm font-bold text-gray-900">{title}</h2>
    </div>
    <div className="space-y-4 px-5 py-4">{children}</div>
  </section>
);

const DetailRow = ({ label, value, multiline = false, emptyText = "—" }) => {
  const isEmpty = value == null || String(value).trim() === "";
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-4">
      <span className="w-40 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </span>
      {isEmpty ? (
        <span className="text-sm italic text-gray-400">{emptyText}</span>
      ) : (
        <p
          className={`text-sm text-gray-800 ${
            multiline ? "whitespace-pre-wrap break-words" : ""
          }`}
        >
          {value}
        </p>
      )}
    </div>
  );
};

export default AutomationDetails;
